import React from 'react';
import { motion } from 'motion/react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface WizardStepProps {
  children: React.ReactNode;
  onNext?: () => void;
  onPrev?: () => void;
  canGoNext?: boolean;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  currentStep: number;
  totalSteps: number;
}

export function WizardStep({
  children,
  onNext,
  onPrev,
  canGoNext = true,
  isFirstStep = false,
  isLastStep = false,
  currentStep,
  totalSteps,
}: WizardStepProps) {
  const { translations: t, language } = useLanguage();
  
  // Translate "Step" based on language
  const stepLabel = language === 'es' ? 'Paso' : language === 'pt' ? 'Passo' : 'Step';
  const ofLabel = language === 'es' ? 'de' : language === 'pt' ? 'de' : 'of';
  
  return (
    <motion.div
      key={currentStep}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <Card className="bg-[#1a1b25] border-[#27272a] shadow-2xl">
        <div className="px-3 py-4 md:p-8 lg:p-12">
          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-3">
              <span className="text-white font-normal">{stepLabel} {currentStep} {ofLabel} {totalSteps}</span>
              <span className="text-[#90d977] font-normal">{Math.round(((currentStep - 1) / (totalSteps - 1)) * 100)}%</span>
            </div>
            <div className="h-2 bg-[#27272a] rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-[#90d977]"
                initial={{ width: 0 }}
                animate={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="min-h-[300px] md:min-h-[400px]">
            {children}
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between items-center mt-8 gap-4">
            {!isFirstStep ? (
              <Button
                onClick={onPrev}
                variant="outline"
                size="lg"
                className="border-[#27272a] bg-[#27272a] text-white hover:bg-[#3f3f46] hover:border-[#3f3f46] hover:text-white cursor-pointer"
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                {t.navigation.previous}
              </Button>
            ) : (
              <div />
            )}
            
            {!isLastStep && (
              <Button
                onClick={onNext}
                disabled={!canGoNext}
                size="lg"
                className="bg-[#90d977] hover:bg-[#a3e887] text-black ml-auto disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isFirstStep ? t.navigation.start : t.navigation.next}
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
